import React, {useState, useCallback, useMemo} from 'react'
import {Emit_main} from "../navigation"
import * as P from "../program-state"
import * as C from "../controls"
import {faFacebook, faLinkedin, faTiktok, faXTwitter} from "@fortawesome/free-brands-svg-icons"
import {IconDefinition} from "@fortawesome/fontawesome-svg-core"
import {useForm} from "react-hook-form"
import {useNavigate} from "react-router-dom"
import {z} from "zod"
import {
    Seniorities,
    StaffCounts,
    JobFunctions,
    GrowthRates,
    Revenues,
    MemberBehaviors,
    map_facet_to_combobox_values
} from "../resource/fixtures"

/**
 * Targeting search responses.
 *
 * Deliberately permissive: these endpoints span four platforms and two
 * generations of the audience api, and return a bare array, a typesense
 * {hits:[{document}]}, or {data:[...]} depending on which. Pinning the fields
 * would break searches that work today.
 *
 * What it does buy over the z.any() this replaces is rejecting a response that
 * is not a collection at all - an html error page, a null, a bare string.
 * Those used to flow straight into the mapping code and surface as "no
 * results", which is indistinguishable from a genuinely empty search.
 */
// Typed loosely on purpose: the call sites below already probe the shape
// (data.hits, data.results, Object.values(data)) because it differs per
// platform, and narrowing here would only push casts into all of them. The
// value is the runtime check, not the static type.
const unifiedSchema = z.union([
    z.array(z.unknown()),
    z.record(z.unknown()),
]) as unknown as z.ZodType<any>;

interface InterestOption {
  key: string;
  display: string;
  source: 'Facebook' | 'TikTok' | 'LinkedIn' | 'X';
  icon: IconDefinition;
}

interface LocationOption {
  key: string;
  display: string;
  platform: string;
  icon: IconDefinition;
  // what facebook actually said about this place. These were dropped during
  // mapping and then hardcoded to the United States further down, so every
  // non-US city was sent to facebook labelled as American.
  country_code?: string;
  country_name?: string;
  region?: string;
}


const Custom_facebook_inputs = ({
    facebookBehaviorOptions,
    facebookLifeEventOptions,
    handleFacebookBehaviorSearch,
    handleFacebookLifeEventSearch,
    setValue,
    watchedValues
}: {
    facebookBehaviorOptions: {key: string, display: string}[],
    facebookLifeEventOptions: {key: string, display: string}[],
    handleFacebookBehaviorSearch: (query: string) => void,
    handleFacebookLifeEventSearch: (query: string) => void,
    setValue: (name: keyof Audience_creation_form, value: any) => void,
    watchedValues: Partial<Audience_creation_form>
}) => {
    return (
        <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center tw-mt-3">
            <C.Eres_form_label htmlFor="eres-fb-life-event">Life event</C.Eres_form_label>
            <C.Eres_multiselect_combobox
                id="eres-fb-life-event"
                data={facebookLifeEventOptions}
                search_placeholder="Search for a life event..."
                empty_selection_text="No life event selected"
                unselect_placeholder="Don't use a life event"
                no_matches_placeholder="No matching life events"
                selectedOptions={watchedValues.facebook__life_events || []}
                onChange={(selected) => setValue('facebook__life_events', selected)}
                onSearch={handleFacebookLifeEventSearch}
            />
            <C.Eres_form_label htmlFor="eres-fb-behavior">Behavior</C.Eres_form_label>
            <C.Eres_multiselect_combobox
                id="eres-fb-behavior"
                data={facebookBehaviorOptions}
                search_placeholder="Search for a behavior..."
                empty_selection_text="No behavior selected"
                unselect_placeholder="Don't use a behavior"
                no_matches_placeholder="No matching behaviors"
                selectedOptions={watchedValues.facebook__behaviors || []}
                onChange={(selected) => setValue('facebook__behaviors', selected)}
                onSearch={handleFacebookBehaviorSearch}
            />
        </div>
    )
}

const Custom_linkedin_inputs = ({
    linkedinJobIndustryOptions,
    linkedinJobTitleOptions,
    handleLinkedinJobIndustrySearch,
    handleLinkedinJobTitleSearch,
    setValue,
    watchedValues
}: {
    linkedinJobIndustryOptions: {key: string, display: string}[],
    linkedinJobTitleOptions: {key: string, display: string}[],
    handleLinkedinJobIndustrySearch: (query: string) => void,
    handleLinkedinJobTitleSearch: (query: string) => void,
    setValue: (name: keyof Audience_creation_form, value: any) => void,
    watchedValues: Partial<Audience_creation_form>
}) => {
    return (
        <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center tw-mt-3">
            <C.Eres_form_label htmlFor="eres-li-seniority">Seniority</C.Eres_form_label>
            <C.Eres_multiselect_combobox
                id="eres-linkedin-seniority"
                data={map_facet_to_combobox_values(Seniorities).map((item, index) => ({
                    key: `seniority-${index}`,
                    display: item.display
                }))}
                search_placeholder="Search for a seniority ..."
                empty_selection_text="No seniority selected"
                unselect_placeholder="Don't use a seniority"
                no_matches_placeholder="No matching seniorities"
                selectedOptions={watchedValues.linkedin__seniorities || []}
                onChange={(selected) => setValue('linkedin__seniorities', selected)}
            />
            <C.Eres_form_label htmlFor="eres-li-staff-count">Staff Count</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-staff-count"
                data={map_facet_to_combobox_values(StaffCounts).map((item, index) => ({
                    key: `staff-${index}`,
                    display: item.display
                }))}
                search_placeholder="Search for a staff count ..."
                empty_selection_text="No staff count selected"
                unselect_placeholder="Don't use a staff count"
                no_matches_placeholder="No matching staff counts"
                defaultValue={(watchedValues.linkedin__staff_counts && watchedValues.linkedin__staff_counts.length > 0) ? watchedValues.linkedin__staff_counts[0] : undefined}
                onChange={(selected) => selected && setValue('linkedin__staff_counts', [selected])}
                onSearch={() => {}} 
            />
            <C.Eres_form_label htmlFor="eres-li-job-function">Job Function</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-job-function"
                data={map_facet_to_combobox_values(JobFunctions)}
                search_placeholder="Search for a job function ..."
                empty_selection_text="No job function selected"
                unselect_placeholder="Don't use a job function"
                no_matches_placeholder="No matching job functions"
                defaultValue={(watchedValues.linkedin__job_functions && watchedValues.linkedin__job_functions.length > 0) ? watchedValues.linkedin__job_functions[0] : undefined}
                onChange={(selected) => selected && setValue('linkedin__job_functions', [selected])}
                onSearch={() => {}} 
            />
            <C.Eres_form_label htmlFor="eres-li-job-industry">Job Industry</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-job-industry"
                data={linkedinJobIndustryOptions}
                search_placeholder="Search for a job industry ..."
                empty_selection_text="No job industry selected"
                unselect_placeholder="Don't use a job industry"
                no_matches_placeholder="No matching job industries"
                defaultValue={(watchedValues.linkedin__job_industries && watchedValues.linkedin__job_industries.length > 0) ? watchedValues.linkedin__job_industries[0] : undefined}
                onSearch={handleLinkedinJobIndustrySearch}
                onChange={(selected) => selected && setValue('linkedin__job_industries', [selected])}
            />
            <C.Eres_form_label htmlFor="eres-li-job-title">Job Title</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-job-title"
                data={linkedinJobTitleOptions}
                search_placeholder="Search for a job title ..."
                empty_selection_text="No job title selected"
                unselect_placeholder="Don't use a job title"
                no_matches_placeholder="No matching job titles"
                defaultValue={(watchedValues.linkedin__job_titles && watchedValues.linkedin__job_titles.length > 0) ? watchedValues.linkedin__job_titles[0] : undefined}
                onSearch={handleLinkedinJobTitleSearch}
                onChange={(selected) => selected && setValue('linkedin__job_titles', [selected])}
            />
            <C.Eres_form_label htmlFor="eres-li-growth-rates">Growth Rates</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-gr"
                data={map_facet_to_combobox_values(GrowthRates).map((item, index) => ({
                    key: `growth-${index}`,
                    display: item.display
                }))}
                search_placeholder="Search for a growth rate ..."
                empty_selection_text="No growth rate selected"
                unselect_placeholder="Don't use a growth rate"
                no_matches_placeholder="No matching growth rates"
                defaultValue={(watchedValues.linkedin__growth_rates && watchedValues.linkedin__growth_rates.length > 0) ? watchedValues.linkedin__growth_rates[0] : undefined}
                onChange={(selected) => selected && setValue('linkedin__growth_rates', [selected])}
                onSearch={() => {}} 
            />
            <C.Eres_form_label htmlFor="eres-li-company-revenue">Company Revenue</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-revenue"
                data={map_facet_to_combobox_values(Revenues)}
                search_placeholder="Search for a revenue ..."
                empty_selection_text="No revenue selected"
                unselect_placeholder="Don't use a revenue"
                no_matches_placeholder="No matching revenues"
                defaultValue={(watchedValues.linkedin__company_revenues && watchedValues.linkedin__company_revenues.length > 0) ? watchedValues.linkedin__company_revenues[0] : undefined}
                onChange={(selected) => selected && setValue('linkedin__company_revenues', [selected])}
                onSearch={() => {}}
            />
            <C.Eres_form_label htmlFor="eres-li-member-traits">Member Traits</C.Eres_form_label>
            <C.Eres_combobox
                id="eres-linkedin-member-traits"
                data={map_facet_to_combobox_values(MemberBehaviors).map((item, index) => ({
                    key: `member-${index}`,
                    display: item.display
                }))}
                search_placeholder="Search for a member trait ..."
                empty_selection_text="No member trait selected"
                unselect_placeholder="Don't use a member trait"
                no_matches_placeholder="No matching member traits"
                defaultValue={(watchedValues.linkedin__member_traits && watchedValues.linkedin__member_traits.length > 0) ? watchedValues.linkedin__member_traits[0] : undefined}
                onChange={(selected) => selected && setValue('linkedin__member_traits', [selected])}
                onSearch={() => {}} 
            />
        </div>
    )
}

const Custom_tiktok_inputs = ({
    tiktokActionCategoryOptions,
    handleTiktokActionCategorySearch,
    setValue,
    watchedValues
}: {
    tiktokActionCategoryOptions: {key: string, display: string}[],
    handleTiktokActionCategorySearch: (query: string) => void,
    setValue: (name: keyof Audience_creation_form, value: any) => void,
    watchedValues: Partial<Audience_creation_form>
}) => {
    return (
        <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center tw-mt-3">
            <C.Eres_form_label htmlFor="eres-tt-action-category">Action categories</C.Eres_form_label>
            <C.Eres_multiselect_combobox
                id="eres-tt-action-category"
                data={tiktokActionCategoryOptions}
                search_placeholder="Search for action categories..."
                empty_selection_text="No action categories selected"
                unselect_placeholder="Don't use action categories"
                no_matches_placeholder="No matching action categories"
                selectedOptions={watchedValues.tiktok__action_categories || []}
                onChange={(selected) => setValue('tiktok__action_categories', selected)}
                onSearch={handleTiktokActionCategorySearch}
            />
        </div>
    )
}

const Custom_x_inputs = ({
    xKeywordOptions,
    handleXKeywordSearch,
    setValue,
    watchedValues
}: {
    xKeywordOptions: {key: string, display: string}[],
    handleXKeywordSearch: (query: string) => void,
    setValue: (name: keyof Audience_creation_form, value: any) => void,
    watchedValues: Partial<Audience_creation_form>
}) => {
    return (
        <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center tw-mt-3">
            <C.Eres_form_label htmlFor="eres-x-keywords">Keywords</C.Eres_form_label>
            <C.Eres_multiselect_combobox
                id="eres-x-keywords"
                data={xKeywordOptions}
                search_placeholder="Search for keywords..."
                empty_selection_text="No keywords selected"
                unselect_placeholder="Don't use keywords"
                no_matches_placeholder="No matching keywords"
                selectedOptions={watchedValues.x__keywords || []}
                onChange={(selected) => setValue('x__keywords', selected)}
                onSearch={handleXKeywordSearch}
            />
        </div>
    )
}

const Custom_network_inputs = ({
    linkedinJobIndustryOptions,
    linkedinJobTitleOptions,
    handleLinkedinJobIndustrySearch,
    handleLinkedinJobTitleSearch,
    facebookBehaviorOptions,
    facebookLifeEventOptions,
    handleFacebookBehaviorSearch,
    handleFacebookLifeEventSearch,
    tiktokActionCategoryOptions,
    handleTiktokActionCategorySearch,
    xKeywordOptions,
    handleXKeywordSearch,
    setValue,
    watchedValues
}: {
    linkedinJobIndustryOptions: {key: string, display: string}[],
    linkedinJobTitleOptions: {key: string, display: string}[],
    handleLinkedinJobIndustrySearch: (query: string) => void,
    handleLinkedinJobTitleSearch: (query: string) => void,
    facebookBehaviorOptions: {key: string, display: string}[],
    facebookLifeEventOptions: {key: string, display: string}[],
    handleFacebookBehaviorSearch: (query: string) => void,
    handleFacebookLifeEventSearch: (query: string) => void,
    tiktokActionCategoryOptions: {key: string, display: string}[],
    handleTiktokActionCategorySearch: (query: string) => void,
    xKeywordOptions: {key: string, display: string}[],
    handleXKeywordSearch: (query: string) => void,
    setValue: (name: keyof Audience_creation_form, value: any) => void,
    watchedValues: Partial<Audience_creation_form>
}) => {
    const tabs: C.TabItem[] = [
        { id: 1, icon: faFacebook, label: "Facebook", content: <Custom_facebook_inputs 
            facebookBehaviorOptions={facebookBehaviorOptions}
            facebookLifeEventOptions={facebookLifeEventOptions}
            handleFacebookBehaviorSearch={handleFacebookBehaviorSearch}
            handleFacebookLifeEventSearch={handleFacebookLifeEventSearch}
            setValue={setValue}
            watchedValues={watchedValues}
        /> },
        { id: 2, icon: faLinkedin, label: "Linkedin", content: <Custom_linkedin_inputs 
            linkedinJobIndustryOptions={linkedinJobIndustryOptions}
            linkedinJobTitleOptions={linkedinJobTitleOptions}
            handleLinkedinJobIndustrySearch={handleLinkedinJobIndustrySearch}
            handleLinkedinJobTitleSearch={handleLinkedinJobTitleSearch}
            setValue={setValue}
            watchedValues={watchedValues}
        /> },
        { id: 3, icon: faTiktok, label: "Tiktok", content: <Custom_tiktok_inputs
            tiktokActionCategoryOptions={tiktokActionCategoryOptions}
            handleTiktokActionCategorySearch={handleTiktokActionCategorySearch}
            setValue={setValue}
            watchedValues={watchedValues}
        /> },
        { id: 4, icon: faXTwitter, label: "X", content: <Custom_x_inputs
            xKeywordOptions={xKeywordOptions}
            handleXKeywordSearch={handleXKeywordSearch}
            setValue={setValue}
            watchedValues={watchedValues}
        /> }
    ];

    return <C.Eres_tab defaultTab={1} tabs={tabs} />
}


type Audience_creation_form = {
    name: string;
    brand_id: number;
    age_from: string;
    age_to: string;
    gender: string;
    linkedin__locations: string[];
    facebook__locations: string[];
    tiktok__locations: string[];
    x__locations: string[];
    facebook__interests: string[];
    linkedin__interests: string[];
    tiktok__interests: string[];
    x__interests: string[];
    include_custom_audience_ids: number[];
    exclude_custom_audience_ids: number[];

    facebook__behaviors: string[];
    facebook__life_events: string[];

    linkedin__seniorities: string[];
    linkedin__staff_counts: string[];
    linkedin__job_functions: string[];
    linkedin__job_industries: string[];
    linkedin__job_titles: string[];
    linkedin__growth_rates: string[];
    linkedin__company_revenues: string[];
    linkedin__member_traits: string[];

    tiktok__action_categories: string[];

    x__keywords: string[];
}

export const Create_audience_form = () => {
    const [brands, ps, cs] = P.use_brands()
    P.run_once_on_component_creation(() => void P.poll_connected_brands(ps, cs))
    const { register, handleSubmit, setValue, watch } = useForm<Audience_creation_form>()
    const watchedValues = watch()
    const navigate = useNavigate()
    const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const platformIcons = {
        facebook: faFacebook,
        linkedin: faLinkedin,
        tiktok: faTiktok,
        x: faXTwitter
    };

    const currentSearchRef = React.useRef<string>('');
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleLocationSearchImmediate = useCallback(async (query: string) => {
        currentSearchRef.current = query;

        if (query.length < 2) {
            if (query.length === 0) {
                setLocationOptions([]);
            }
            setIsLocationLoading(false);
            return;
        }

        setIsLocationLoading(true);
        const platforms = ['facebook', 'linkedin', 'tiktok', 'x'];

        try {
            const results = await Promise.all(platforms.map(async (platform) => {
                try {
                    let apiUrl: string;
                    if (platform === 'tiktok') {
                        apiUrl = P.v1api`audiencesv2/search/${platform}/region?query=${query}`;
                    } else if (platform === 'x') {
                        apiUrl = P.v1api`audiencesv2/search/${platform}/location?query=${query}`;
                    } else {
                        apiUrl = P.v1api`audiencesv2/search/${platform}/location?query=${query}`;
                    }

                    if (!cs.token) {
                        return [];
                    }

                    let ok: boolean, data: any;
                    try {
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('API call timeout')), 5000)
                        );

                        const apiPromise = P.issue_json_get(apiUrl, unifiedSchema, cs.token);

                        [ok, data] = await Promise.race([apiPromise, timeoutPromise]) as [boolean, any];
                    } catch (error) {
                        return [];
                    }


                    if (ok) {
                        let platformResults: LocationOption[] = [];
                        if (platform === 'tiktok') {
                            const tiktokData = data.hits || [];
                            platformResults = tiktokData.map((item: any) => {
                                const name = item.document?.name || '';
                                return {
                                    key: `tiktok-${item.document?.id || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                    display: name,
                                    platform: 'tiktok',
                                    icon: platformIcons.tiktok
                                };
                            });
                        } else if (platform === 'x') {
                            const xData = Array.isArray(data) ? data : [];
                            platformResults = xData.map((item: any) => ({
                                key: `x-${item.targeting_value || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                display: item.name || '',
                                platform: 'x',
                                icon: platformIcons.x
                            }));
                        } else {
                            if (Array.isArray(data)) {
                                platformResults = data.map((item: any) => ({
                                    key: `${platform}-${item.id || item.key || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                    display: item.name || item.display || '',
                                    platform: platform,
                                    icon: platformIcons[platform as keyof typeof platformIcons],
                                    country_code: item.country_code,
                                    country_name: item.country_name,
                                    region: item.region
                                }));
                            } else if (data && typeof data === 'object') {
                                const items = data.hits || data.results || Object.values(data);
                                if (Array.isArray(items)) {
                                    platformResults = items.map((item: any) => ({
                                        key: `${platform}-${item.id || item.key || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                        display: item.name || item.display || '',
                                        platform: platform,
                                        icon: platformIcons[platform as keyof typeof platformIcons]
                                    }));
                                }
                            }
                        }
                        return platformResults;
                    }
                } catch (error) {
                    return [];
                }
                return [];
            }));

            // flatten and deduplicate results before setting state
            let allResults = results.flat().filter(Boolean);

            const facebookResults = allResults.filter(item => item.platform === 'facebook');
            const deduplicatedFacebookResults = facebookResults.filter((v, i, a) => a.findIndex(t => t.display === v.display) === i);

            const linkedInResults = allResults.filter(item => item.platform === 'linkedin');
            const tiktokResults = allResults.filter(item => item.platform === 'tiktok');
            const xResults = allResults.filter(item => item.platform === 'x');

            allResults = [...deduplicatedFacebookResults, ...linkedInResults, ...tiktokResults, ...xResults];

            // only update state if this is still the current search
            if (currentSearchRef.current === query) {
                setLocationOptions(allResults);
            }

        } catch (error) {
            console.error('Error fetching locations:', error);
        } finally {
            // only turn off loading if this is still the current search
            if (currentSearchRef.current === query) {
                setIsLocationLoading(false);
            }
        }
    }, [cs.token]);

    const handleLocationSearch = useCallback((query: string) => {
        // clear timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // show loading immediately
        if (query.length >= 2) {
            setIsLocationLoading(true);
        }

        // debounce the actual search
        debounceTimerRef.current = setTimeout(() => {
            void handleLocationSearchImmediate(query);
        }, 800);
    }, [handleLocationSearchImmediate]);

    // Errors used to be alert() boxes, which cannot be styled, cannot be
    // dismissed in place, and read as a browser fault rather than ours.
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [interestOptions, setInterestOptions] = useState<InterestOption[]>([]);
    // Every interest option we have shown so far, keyed by option key.
    // interestOptions is replaced wholesale on each search, and selections were
    // filtered against it - so searching a second time silently dropped
    // everything picked during the first search, both from the chips and from
    // the submitted form value.
    const [seenInterestOptions, setSeenInterestOptions] = useState<InterestOption[]>([]);
    const rememberInterestOptions = (options: InterestOption[]) => {
        setSeenInterestOptions(previous => {
            const by_key = new Map(previous.map(o => [o.key, o]));
            options.forEach(o => by_key.set(o.key, o));
            return Array.from(by_key.values());
        });
    };

    // What the combobox renders: the current search results, plus anything
    // already chosen, so a selection keeps its chip after a later search.
    const interestOptionsForDisplay = useMemo(() => {
        const chosen = new Set<string>([
            ...(Array.isArray(watchedValues.facebook__interests) ? watchedValues.facebook__interests : []),
            ...(Array.isArray(watchedValues.linkedin__interests) ? watchedValues.linkedin__interests : []),
            ...(Array.isArray(watchedValues.tiktok__interests) ? watchedValues.tiktok__interests : []),
            ...(Array.isArray(watchedValues.x__interests) ? watchedValues.x__interests : []),
        ].filter((k): k is string => typeof k === 'string' && k.length > 0));

        const by_key = new Map(interestOptions.map(o => [o.key, o]));
        seenInterestOptions.forEach(o => { if (chosen.has(o.key)) by_key.set(o.key, o); });
        return Array.from(by_key.values());
    }, [interestOptions, seenInterestOptions,
        watchedValues.facebook__interests, watchedValues.linkedin__interests,
        watchedValues.tiktok__interests, watchedValues.x__interests]);
    const [isInterestLoading, setIsInterestLoading] = useState(false);

    const currentInterestSearchRef = React.useRef<string>('');
    const debounceInterestTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleInterestSearchImmediate = useCallback(async (query: string) => {
        currentInterestSearchRef.current = query;

        if (query.length < 2) {
            if (query.length === 0) {
                setInterestOptions([]);
            }
            setIsInterestLoading(false);
            return;
        }

        setIsInterestLoading(true);
        const platforms = ['facebook', 'linkedin', 'tiktok', 'x'];

        try {
            const results = await Promise.all(platforms.map(async (platform) => {
                try {
                    const apiUrl = platform === 'linkedin'
                        ? P.v1api`audiences/search/${platform}/interest?query=${query}`
                        : P.v1api`audiencesv2/search/${platform}/interest?query=${query}`;

                    const [ok, data] = await P.issue_json_get(apiUrl, unifiedSchema, cs.token);

                    if (ok) {
                        let platformResults: InterestOption[] = [];
                        if (platform === 'tiktok') {
                            const tiktokData = data.hits || [];
                            platformResults = tiktokData.map((item: any) => {
                                const name = item.document?.interest_category_name || '';
                                return {
                                    key: `tiktok-${item.document?.id || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                    display: name,
                                    source: 'TikTok',
                                    icon: platformIcons.tiktok
                                };
                            });
                        } else if (platform === 'x') {
                            const xData = Array.isArray(data) ? data : [];
                            platformResults = xData.map((item: any) => ({
                                key: `x-${item.targeting_value || ''}-${Math.random().toString(36).substring(2, 11)}`,
                                display: item.name || '',
                                source: 'X' as const,
                                icon: platformIcons.x
                            }));
                        } else {
                            // handle different response structures
                            let itemsToMap: any[] = [];

                            if (Array.isArray(data)) {
                                // linkedin returns direct array
                                itemsToMap = data;
                            } else if (data && typeof data === 'object') {
                                // facebook/tiktok return {hits: [...]} structure
                                itemsToMap = data.hits || data.results || [];
                            }

                            if (itemsToMap.length > 0) {
                                platformResults = itemsToMap.map((item: any) => {
                                    let display = '';
                                    let id = '';

                                    if (platform === 'facebook') {
                                        // facebook data is nested in document
                                        display = item.document?.name || item.document?.display_name || item.name || item.display || '';
                                        id = item.document?.id || item.id || item.key || '';
                                    } else {
                                        // linkedin uses direct properties
                                        display = item.name || item.display || '';
                                        id = item.id || item.key || '';
                                    }

                                    return {
                                        key: `${platform}-${id}-${Math.random().toString(36).substring(2, 11)}`,
                                        display: display,
                                        source: platform === 'facebook' ? 'Facebook' : platform === 'linkedin' ? 'LinkedIn' : 'TikTok',
                                        icon: platformIcons[platform as keyof typeof platformIcons]
                                    };
                                });
                            }
                        }
                        return platformResults;
                    }
                } catch (error) {
                    return [];
                }
                return [];
            }));

            // flatten results before setting state
            const allResults = results.flat().filter(Boolean);

            // only update state if this is still the current search
            if (currentInterestSearchRef.current === query) {
                setInterestOptions(allResults);
                rememberInterestOptions(allResults);
            }

        } catch (error) {
            console.error('Error fetching interests:', error);
        } finally {
            // only turn off loading if this is still the current search
            if (currentInterestSearchRef.current === query) {
                setIsInterestLoading(false);
            }
        }
    }, [cs.token]);

    const handleInterestSearch = useCallback((query: string) => {
        // clear timer
        if (debounceInterestTimerRef.current) {
            clearTimeout(debounceInterestTimerRef.current);
        }

        // show loading immediately
        if (query.length >= 2) {
            setIsInterestLoading(true);
        }

        // debounce the actual search
        debounceInterestTimerRef.current = setTimeout(() => {
            void handleInterestSearchImmediate(query);
        }, 800);
    }, [handleInterestSearchImmediate]);

    // linkedin additional search handlers
    const [linkedinJobIndustryOptions, setLinkedinJobIndustryOptions] = useState<{key: string, display: string}[]>([]);
    const [linkedinJobTitleOptions, setLinkedinJobTitleOptions] = useState<{key: string, display: string}[]>([]);

    const handleLinkedinJobIndustrySearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setLinkedinJobIndustryOptions([]);
            return;
        }
        try {
            const [ok, data] = await P.issue_json_get(P.v1api`audiences/search/linkedin/job_industry?query=${query}`, unifiedSchema, cs.token);
            if (ok && Array.isArray(data)) {
                const results = data.map((item: any, index: number) => ({
                    key: item.id || item.key || `job-industry-${index}`,
                    display: item.name || item.display || ''
                }));
                setLinkedinJobIndustryOptions(results);
            }
        } catch (error) {

        }
    }, [cs.token]);

    const handleLinkedinJobTitleSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setLinkedinJobTitleOptions([]);
            return;
        }
        try {
            const [ok, data] = await P.issue_json_get(P.v1api`audiences/search/linkedin/job_title?query=${query}`, unifiedSchema, cs.token);
            if (ok && Array.isArray(data)) {
                const results = data.map((item: any, index: number) => ({
                    key: item.id || item.key || `job-title-${index}`,
                    display: item.name || item.display || ''
                }));
                setLinkedinJobTitleOptions(results);
            }
        } catch (error) {
        }
    }, [cs.token]);

    // facebook search handlers
    const [facebookBehaviorOptions, setFacebookBehaviorOptions] = useState<{key: string, display: string}[]>([]);
    const [facebookLifeEventOptions, setFacebookLifeEventOptions] = useState<{key: string, display: string}[]>([]);

    const handleFacebookBehaviorSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setFacebookBehaviorOptions([]);
            return;
        }
        try {
            const [ok, data] = await P.issue_json_get(P.v1api`audiencesv2/search/facebook/behavior?query=${query}`, unifiedSchema, cs.token);
            if (ok) {
                let results: {key: string, display: string}[] = [];
                const itemsToMap = data.hits || data.results || [];
                if (itemsToMap.length > 0) {
                    results = itemsToMap.map((item: any) => ({
                        key: item.document?.id || item.id || item.key || '',
                        display: item.document?.name || item.document?.display_name || item.name || item.display || ''
                    }));
                }
                setFacebookBehaviorOptions(results);
            }
        } catch (error) {
        }
    }, [cs.token]);

    const handleFacebookLifeEventSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setFacebookLifeEventOptions([]);
            return;
        }
        try {
            const [ok, data] = await P.issue_json_get(P.v1api`audiencesv2/search/facebook/life-events?query=${query}`, unifiedSchema, cs.token);
            if (ok) {
                let results: {key: string, display: string}[] = [];
                const itemsToMap = data.hits || data.results || [];
                if (itemsToMap.length > 0) {
                    results = itemsToMap.map((item: any) => ({
                        key: item.document?.id || item.id || item.key || '',
                        display: item.document?.name || item.document?.display_name || item.name || item.display || ''
                    }));
                }
                setFacebookLifeEventOptions(results);
            }
        } catch (error) {
        }
    }, [cs.token]);

    // tiktok search handlers
    const [tiktokActionCategoryOptions, setTiktokActionCategoryOptions] = useState<{key: string, display: string}[]>([]);

    const handleTiktokActionCategorySearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setTiktokActionCategoryOptions([]);
            return;
        }
        try {
            const [ok, data] = await P.issue_json_get(P.v1api`audiencesv2/search/tiktok/action?query=${query}`, unifiedSchema, cs.token);
            if (ok) {
                let results: {key: string, display: string}[] = [];
                const itemsToMap = data.hits || data.results || [];
                if (itemsToMap.length > 0) {
                    results = itemsToMap.map((item: any) => ({
                        key: item.document?.id || item.id || item.key || '',
                        display: item.document?.name || item.document?.display_name || item.name || item.display || ''
                    }));
                }
                setTiktokActionCategoryOptions(results);
            }
        } catch (error) {

        }
    }, [cs.token]);

    // x search handlers
    const [xKeywordOptions, setXKeywordOptions] = useState<{key: string, display: string}[]>([]);

    const handleXKeywordSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setXKeywordOptions([]);
            return;
        }
        // we can just return the query as an option
        // x keyword targeting doesn't require a search api, user can input any keyword
        // https://developer.x.com/en/docs/x-ads-api/campaign-management/guides/keyword-targeting-timelines
        setXKeywordOptions([{key: query, display: query}]);
    }, []);

    const onSubmit = async (data: Audience_creation_form) => {
        setIsSubmitting(true);
        
        try {
            // transform form data to API payload format
            const payload = {

                name: data.name || '',
                brand_id: data.brand_id || 0,
                age_from: data.age_from || '18',
                age_to: data.age_to || '65',
                gender: data.gender === 'male' ? '1' : data.gender === 'female' ? '2' : '0',

                // facebook targeting - JSON-string
                facebook__interests: transformSearchDataToJSON(data.facebook__interests, seenInterestOptions, 'facebook'),
                facebook__locations: transformLocationDataToJSON(data.facebook__locations, locationOptions, 'facebook'),
                facebook__behaviors: transformSearchDataToJSON(data.facebook__behaviors, facebookBehaviorOptions, 'facebook'),
                facebook__life_events: transformSearchDataToJSON(data.facebook__life_events, facebookLifeEventOptions, 'facebook'),

                // linkedin targeting
                linkedin__interests: transformSearchDataToJSON(data.linkedin__interests, seenInterestOptions, 'linkedin'),
                linkedin__locations: transformLocationDataToJSON(data.linkedin__locations, locationOptions, 'linkedin'),

                // linkedin static selections - URN strings directly
                linkedin__seniorities: transformStaticDataToURNs(data.linkedin__seniorities, Seniorities),
                linkedin__staff_counts: transformSingleSelectionToURN(data.linkedin__staff_counts, StaffCounts),
                linkedin__job_functions: transformStaticDataToURNs(data.linkedin__job_functions, JobFunctions),
                linkedin__job_industries: data.linkedin__job_industries || [],
                linkedin__job_titles: data.linkedin__job_titles || [],
                linkedin__growth_rates: transformStaticDataToURNs(data.linkedin__growth_rates, GrowthRates),
                linkedin__company_revenues: transformStaticDataToURNs(data.linkedin__company_revenues, Revenues),
                linkedin__member_traits: transformStaticDataToURNs(data.linkedin__member_traits, MemberBehaviors),

                // tiktok targeting - JSON-string
                tiktok__interests: transformSearchDataToJSON(data.tiktok__interests, seenInterestOptions, 'tiktok'),
                tiktok__locations: transformLocationDataToJSON(data.tiktok__locations, locationOptions, 'tiktok'),
                tiktok__action_categories: transformActionCategoryDataToJSON(data.tiktok__action_categories, tiktokActionCategoryOptions),

                // x targeting - JSON-string
                x__interests: transformSearchDataToJSON(data.x__interests, seenInterestOptions, 'x'),
                x__locations: transformLocationDataToJSON(data.x__locations, locationOptions, 'x'),
                x__keywords: data.x__keywords || [],

                // custom audiences (not implemented in form yet)
                include_custom_audience_ids: data.include_custom_audience_ids || [],
                exclude_custom_audience_ids: data.exclude_custom_audience_ids || []
            };

            // make API call
            const [ok, result] = await P.issue_json_post(
                P.v1api`audiences`, 
                payload,
                z.any(), 
                cs.token
            );

            if (ok) {
                // redirect to audiences listing page
                navigate('/audiences');
            } else {
                // handle API errors gracefully
                if (result instanceof P.http_error) {
                    try {
                        const errorText = await result.resp.text();
                        console.error('Backend error response:', errorText);
                        setSubmitError(`We could not save this audience: ${errorText}`);
                    } catch (e) {
                        console.error('Could not read error response:', e);
                        setSubmitError('We could not save this audience. Please check the form and try again.');
                    }
                } else {
                    console.error('Failed to create audience:', result);
                    setSubmitError('We could not save this audience. Please check the form and try again.');
                }
            }
            
        } catch (error) {
            console.error('Error submitting audience:', error);
            setSubmitError('Something went wrong while saving this audience.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // helper functions for data 
    const transformSearchDataToJSON = (keys: string[] | undefined, dataOptions: any[], platform?: string) => {
        if (!keys || !Array.isArray(keys)) return [];
        
        return keys.map(key => {
            // find the matching data item
            const item = dataOptions.find(option => option.key === key);
            if (!item) return null;
            
            // for search-based data, we need to reconstruct the original API object format
            // the key format is typically "platform-id-random" or "platform--id-random" (double dash for some platforms)
            const parts = key.split('-');
            if (parts.length >= 2) {
                const platformPrefix = parts[0];
                // handle double dash format (like "linkedin--abc") where parts[1] might be empty
                const id = parts[1] || (parts.length >= 3 ? parts[2] : '');
                
                // debug logging to check the key parsing
                
                // create the expected API object format based on platform
                let apiObject;
                if (platformPrefix === 'linkedin') {
                    // linkedin interests need URN and facetUrn format
                    apiObject = {
                        urn: `urn:li:interest:${id}`,
                        name: item.display,
                        facetUrn: "urn:li:adTargetingFacet:interests"
                    };
                } else if (platformPrefix === 'facebook') {
                    // facebook format
                    apiObject = {
                        id: id,
                        name: item.display,
                        path: [item.display]
                    };
                } else if (platformPrefix === 'tiktok') {
                    // tiktok interests format
                    apiObject = {
                        interest_category_id: id,
                        interest_category_name: item.display
                    };
                } else if (platformPrefix === 'x') {
                    // x interests format
                    apiObject = {
                        id: id,
                        name: item.display
                    };
                } else {
                    // default format
                    apiObject = {
                        id: id,
                        name: item.display
                    };
                }
                
                return JSON.stringify(apiObject);
            }
            
            // fallback for items that don't match expected format
            return JSON.stringify({
                id: key,
                name: item.display || key
            });
        }).filter(Boolean);
    };

    const transformLocationDataToJSON = (keys: string[] | undefined, locationOptions: any[], platform: string) => {
        if (!keys || !Array.isArray(keys)) return [];
        
        return keys.map(key => {
            // find the matching location item
            const item = locationOptions.find(option => option.key === key);
            if (!item) return null;
            
            // extract ID from key format "platform-id-random" or "platform--id-random" 
            const parts = key.split('-');
            if (parts.length >= 2) {
                // handle double dash format where parts[1] might be empty
                const id = parts[1] || (parts.length >= 3 ? parts[2] : '');
                
                // debug logging for location transformation
                
                // create location object based on platform
                let locationObject;
                if (platform === 'facebook') {
                    locationObject = {
                        key: id,
                        name: item.display,
                        // fall back to the display name / US only when facebook
                        // told us nothing, rather than always claiming the US
                        region: item.region ?? item.display,
                        country_name: item.country_name ?? "United States",
                        country_code: item.country_code ?? "US"
                    };
                } else if (platform === 'linkedin') {
                    locationObject = {
                        urn: `urn:li:geo:${id}`,
                        name: item.display
                    };
                } else if (platform === 'tiktok') {
                    locationObject = {
                        location_id: id,
                        name: item.display,
                        region_code: "Unknown"
                    };
                } else if (platform === 'x') {
                    locationObject = {
                        id: id,
                        name: item.display
                    };
                }

                if (locationObject) {
                    return JSON.stringify(locationObject);
                }
                return null;
            }
            
            // fallback
            return JSON.stringify({
                id: key,
                name: item.display || key
            });
        }).filter(Boolean);
    };

    const transformActionCategoryDataToJSON = (keys: string[] | undefined, dataOptions: any[]) => {
        if (!keys || !Array.isArray(keys)) return [];
        
        return keys.map(key => {
            // find the matching data item
            const item = dataOptions.find(option => option.key === key);
            if (!item) return null;
            
            // extract ID from tiktok action category key
            const parts = key.split('-');
            let id = key; // Default to the key itself for simple IDs
            
            // handle compound keys (like 'tiktok-362-xyz') vs simple IDs (like '362')
            if (parts.length >= 2) {
                id = parts[1] || (parts.length >= 3 ? parts[2] : '');
            }
            
            // create tiktok action category object - always include action_scene field
            const actionObject = {
                action_category_id: id,
                name: item.display,
                description: item.display || 'Action category',
                action_scene: 'UNSPECIFIED' 
            };
            
            return JSON.stringify(actionObject);
        }).filter(Boolean);
    };

    const transformStaticDataToURNs = (keys: string[] | undefined, staticData: any[]) => {
        if (!keys || !Array.isArray(keys)) return [];
        
        return keys.map(key => {
            // for static data, the key is in format "type-index", we need to get the URN
            const index = parseInt(key.split('-')[1]);
            if (isNaN(index) || !staticData[index]) return null;
            return staticData[index].urn;
        }).filter(Boolean);
    };

    const transformSingleSelectionToURN = (keys: string[] | undefined, staticData: any[]) => {
        const urns = transformStaticDataToURNs(keys, staticData);
        return urns.length > 0 ? urns[0] : '';
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-4">
            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-name">Name</C.Eres_form_label>
                <C.Eres_line_edit id="eres-name" aria-label="name" type="text" {...register("name")} />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-brand">Brand</C.Eres_form_label>
                <C.Eres_combobox
                    id="eres-brand"
                    data={(() => {
                        try {
                            if (!brands) {
                                return [];
                            }
                            if (!Array.isArray(brands)) {
                                return [];
                            }
                            return brands.map(brand => ({
                                key: brand?.id?.toString() || '',
                                display: brand?.name || 'Unnamed Brand'
                            })).filter(item => item.key && item.display);
                        } catch (error) {
                            console.warn('Error processing brands data:', error);
                            return [];
                        }
                    })()}
                    search_placeholder={brands ? "Search for a brand ..." : "Loading brands..."}
                    empty_selection_text={brands ? "No brand selected" : "Brands loading..."}
                    unselect_placeholder="Don't use a brand"
                    no_matches_placeholder={brands ? "No matching brands" : "No brands available"}
                    defaultValue={watchedValues.brand_id?.toString()}
                    onChange={(selected) => {
                        try {
                            if (selected && !isNaN(parseInt(selected))) {
                                setValue("brand_id", parseInt(selected));
                            }
                        } catch (error) {
                            console.warn('Error setting brand_id:', error);
                        }
                    }}
                />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-age-from">Age from</C.Eres_form_label>
                <C.Eres_line_edit id="eres-age-from" aria-label="age from" type="text" {...register("age_from", { valueAsNumber: true })} />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-age-to">Age to</C.Eres_form_label>
                <C.Eres_line_edit id="eres-age-to" aria-label="age to" type="text" {...register("age_to")} />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-gender">Gender</C.Eres_form_label>
                <C.Eres_combobox
                    id="eres-gender"
                    data={[
                        { key: "male", display: "Male" },
                        { key: "female", display: "Female" },
                        { key: "other", display: "Other" },
                    ]}
                    search_placeholder="Search for a gender ..."
                    empty_selection_text="No gender selected"
                    unselect_placeholder="Don't use a gender"
                    no_matches_placeholder="No matching genders"
                    defaultValue={watchedValues.gender}
                    onChange={(selected) => selected && setValue("gender", selected)}
                />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-locations">Locations</C.Eres_form_label>
                <C.Eres_multiselect_combobox
                    id="eres-locations"
                    data={locationOptions || []}
                    selectedOptions={(() => {
                        try {
                            const allLocations = [
                                ...(Array.isArray(watchedValues.facebook__locations) ? watchedValues.facebook__locations : []),
                                ...(Array.isArray(watchedValues.linkedin__locations) ? watchedValues.linkedin__locations : []),
                                ...(Array.isArray(watchedValues.tiktok__locations) ? watchedValues.tiktok__locations : []),
                                ...(Array.isArray(watchedValues.x__locations) ? watchedValues.x__locations : [])
                            ].filter(key => {
                                // only include valid string keys
                                return typeof key === 'string' && key.length > 0;
                            });
                            return allLocations;
                        } catch (error) {
                            console.warn('Error processing location selectedOptions:', error);
                            return [];
                        }
                    })()}
                    onChange={(selected) => {
                        try {
                            // ensure selected is an array of valid strings
                            const validSelected = Array.isArray(selected) ? 
                                selected.filter(key => typeof key === 'string' && key.includes('-')) : [];

                            // group selections by platform based on key prefix
                            const groupedSelections = validSelected.reduce((acc: Record<string, string[]>, key) => {
                                const parts = key.split('-');
                                if (parts.length >= 2) {
                                    const platform = parts[0];
                                    if (!acc[platform]) acc[platform] = [];
                                    acc[platform].push(key);
                                }
                                return acc;
                            }, { facebook: [], linkedin: [], tiktok: [], x: [] });

                            setValue('facebook__locations', groupedSelections.facebook);
                            setValue('linkedin__locations', groupedSelections.linkedin);
                            setValue('tiktok__locations', groupedSelections.tiktok);
                            setValue('x__locations', groupedSelections.x);
                        } catch (error) {
                            console.warn('Error processing location onChange:', error);
                        }
                    }}
                    onSearch={handleLocationSearch}
                    isLoading={isLocationLoading}
                    search_placeholder="Search for locations..."
                    no_matches_placeholder="No matching locations"
                    empty_selection_text="Select locations"
                    unselect_placeholder="Clear selection"
                />
            </div>

            <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                <C.Eres_form_label htmlFor="eres-interests">Interests</C.Eres_form_label>
                <C.Eres_multiselect_combobox
                    id="eres-interests"
                    data={interestOptionsForDisplay}
                    search_placeholder="Search for interests..."
                    empty_selection_text="No interests selected"
                    unselect_placeholder="Don't use interests"
                    no_matches_placeholder="No matching interests"
                    selectedOptions={useMemo(() => {
                        try {
                            const allInterests = [
                                ...(Array.isArray(watchedValues.facebook__interests) ? watchedValues.facebook__interests : []),
                                ...(Array.isArray(watchedValues.linkedin__interests) ? watchedValues.linkedin__interests : []),
                                ...(Array.isArray(watchedValues.tiktok__interests) ? watchedValues.tiktok__interests : []),
                                ...(Array.isArray(watchedValues.x__interests) ? watchedValues.x__interests : [])
                            ].filter(key => {
                                // only include keys that exist in array and are valid strings
                                return typeof key === 'string' && 
                                       key.length > 0 && 
                                       seenInterestOptions.some(item => item.key === key);
                            });
                            return allInterests;
                        } catch (error) {
                            console.warn('Error processing interest selectedOptions:', error);
                            return [];
                        }
                    }, [watchedValues.facebook__interests, watchedValues.linkedin__interests, watchedValues.tiktok__interests, watchedValues.x__interests, seenInterestOptions])}
                    isLoading={isInterestLoading}
                    onChange={(selected) => {
                        try {
                            // ensure selected is an array of valid strings
                            const validSelected = Array.isArray(selected) ? 
                                selected.filter(key => typeof key === 'string' && key.includes('-')) : [];

                            // group the currently selected items by platform
                            const groupedSelections = validSelected.reduce((acc: Record<string, string[]>, key) => {
                                const parts = key.split('-');
                                if (parts.length >= 2) {
                                    const platform = parts[0];
                                    if (!acc[platform]) acc[platform] = [];
                                    acc[platform].push(key);
                                }
                                return acc;
                            }, { facebook: [], linkedin: [], tiktok: [], x: [] });

                            // update form state for each platform
                            setValue('facebook__interests', groupedSelections.facebook);
                            setValue('linkedin__interests', groupedSelections.linkedin);
                            setValue('tiktok__interests', groupedSelections.tiktok);
                            setValue('x__interests', groupedSelections.x);
                        } catch (error) {
                            console.warn('Error processing interest onChange:', error);
                        }
                    }}
                    onSearch={handleInterestSearch}
                />
            </div>

            <Custom_network_inputs
                linkedinJobIndustryOptions={linkedinJobIndustryOptions}
                linkedinJobTitleOptions={linkedinJobTitleOptions}
                handleLinkedinJobIndustrySearch={handleLinkedinJobIndustrySearch}
                handleLinkedinJobTitleSearch={handleLinkedinJobTitleSearch}
                facebookBehaviorOptions={facebookBehaviorOptions}
                facebookLifeEventOptions={facebookLifeEventOptions}
                handleFacebookBehaviorSearch={handleFacebookBehaviorSearch}
                handleFacebookLifeEventSearch={handleFacebookLifeEventSearch}
                tiktokActionCategoryOptions={tiktokActionCategoryOptions}
                handleTiktokActionCategorySearch={handleTiktokActionCategorySearch}
                xKeywordOptions={xKeywordOptions}
                handleXKeywordSearch={handleXKeywordSearch}
                setValue={setValue}
                watchedValues={watchedValues}
            />

            {submitError !== null && (
                <div role="alert" className="tw-mb-3 tw-rounded tw-border tw-border-red-300 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-800">
                    {submitError}
                </div>
            )}

            <div className="tw-flex tw-justify-end">
                <C.Eres_button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create audience'}
                </C.Eres_button>
            </div>
        </form>
    )
};
const Create_audience: React.FC = () => {
    return (
        <Emit_main page_id="PAGE-audiences">
            <Create_audience_form />
        </Emit_main>
    )
};

export default Create_audience;